import React, { useEffect, useState, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../services/authContext';
import { apiService } from '../services/apiService';
import { portalService } from '../services/portalService';

const DAYS      = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const DAY_COLORS = { Monday:'#1976D2', Tuesday:'#388E3C', Wednesday:'#F57C00', Thursday:'#7B1FA2', Friday:'#D32F2F', Saturday:'#546E7A' };

const MyClasses = () => {
  const { user }                  = useAuth();
  const [student, setStudent]     = useState(null);
  const [modules, setModules]     = useState([]);
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [activeTab, setActiveTab] = useState('modules');

  const loadData = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true); setError('');
      const studentData = await portalService.getCurrentStudent(user.id);
      setStudent(studentData);

      const [moduleResponses, ttData] = await Promise.all([
        Promise.all((studentData.classIds || []).map(id => apiService.get(`modules/class/${id}`))),
        studentData.classIds?.length
          ? apiService.get(`timetable?classId=${studentData.classIds[0]}`)
          : Promise.resolve([]),
      ]);

      setModules(moduleResponses.flat());
      setTimetable(Array.isArray(ttData) ? ttData : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load your class information.');
    } finally { setLoading(false); }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const byDay = DAYS.reduce((acc, day) => {
    acc[day] = timetable.filter(e => e.dayOfWeek === day).sort((a, b) => a.startTime.localeCompare(b.startTime));
    return acc;
  }, {});

  if (loading) return <LoadingSpinner message="Loading your programme..." />;

  return (
    <div className="page-container">
      <div className="page-header"><h1>My Programme</h1></div>

      {error && <div className="error-message">{error}</div>}

      {!student ? (
        <div className="no-data-message">No class record found for your account.</div>
      ) : (
        <>
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '1.25rem', marginBottom: '1.5rem' }}>
            <h2 style={{ margin: '0 0 0.75rem' }}>{student.className || 'Not assigned to a class'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
              {[
                { label: 'Name',          value: `${student.firstName} ${student.lastName}` },
                { label: 'Email',         value: student.email },
                { label: 'Year Level',    value: student.grade },
                { label: 'Date of Birth', value: student.dob ? new Date(student.dob).toLocaleDateString() : 'N/A' },
              ].map(f => (
                <div key={f.label}>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-light)', marginBottom: '0.15rem' }}>{f.label}</div>
                  <div style={{ fontWeight: 600 }}>{f.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="messages-tabs" style={{ marginBottom: '1.5rem' }}>
            <button className={`tab-button ${activeTab === 'modules' ? 'active' : ''}`} onClick={() => setActiveTab('modules')}>
              Modules ({modules.length})
            </button>
            <button className={`tab-button ${activeTab === 'timetable' ? 'active' : ''}`} onClick={() => setActiveTab('timetable')}>
              Timetable {timetable.length > 0 ? `(${timetable.length})` : ''}
            </button>
          </div>

          {activeTab === 'modules' && (
            modules.length === 0 ? (
              <div className="no-data-message">No modules assigned to your class yet.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1rem' }}>
                {modules.map(m => (
                  <div key={`${m._id}-${m.classId || ''}`} style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '1rem' }}>
                    <span style={{ background: '#e3f2fd', color: '#1565c0', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.78rem', fontWeight: 700 }}>{m.code}</span>
                    <div style={{ fontWeight: 600, marginTop: '0.4rem', fontSize: '1rem' }}>{m.name}</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginTop: '0.2rem' }}>👨🏫 {m.teacherName || 'Not assigned'}</div>
                    {m.description && <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginTop: '0.3rem' }}>{m.description}</div>}
                  </div>
                ))}
              </div>
            )
          )}

          {activeTab === 'timetable' && (
            timetable.length === 0 ? (
              <div className="no-data-message">No timetable has been set up for your class yet.</div>
            ) : (
              <div>
                {DAYS.map(day => (
                  byDay[day].length === 0 ? null : (
                    <div key={day} style={{ marginBottom: '1.5rem' }}>
                      <h3 style={{ color: DAY_COLORS[day], borderBottom: `2px solid ${DAY_COLORS[day]}`, paddingBottom: '0.35rem', marginBottom: '0.75rem' }}>{day}</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {byDay[day].map(e => (
                          <div key={e.id} style={{ background: 'var(--card-bg)', border: `1px solid ${DAY_COLORS[day]}44`, borderLeft: `4px solid ${DAY_COLORS[day]}`, borderRadius: 8, padding: '0.85rem 1rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ minWidth: 90, fontWeight: 700, color: DAY_COLORS[day] }}>{e.startTime} – {e.endTime}</div>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontWeight: 600 }}>{e.moduleCode || e.subjectCode} — {e.moduleName || e.subjectName}</span>
                              {e.teacherName && <span style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginLeft: '0.75rem' }}>👨🏫 {e.teacherName}</span>}
                              {e.room && <span style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginLeft: '0.75rem' }}>📍 {e.room}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ))}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
};

export default MyClasses;
